/**
 * PromoCodeInput — Wiederverwendbare Gutschein-Code-Eingabe
 * 
 * Zeigt ein Eingabefeld mit "Einlösen"-Button.
 * Nach erfolgreicher Einlösung wird die Seite neu geladen.
 */
import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Gift, Loader2, CheckCircle, XCircle } from "lucide-react";

interface PromoCodeInputProps {
  /** Kompakter Modus (z.B. für Sidebar) */
  compact?: boolean;
  /** Callback nach erfolgreicher Einlösung */
  onSuccess?: () => void;
}

export default function PromoCodeInput({ compact = false, onSuccess }: PromoCodeInputProps) {
  const [code, setCode] = useState("");
  const [showInput, setShowInput] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  const redeemMutation = trpc.promo.redeem.useMutation({
    onSuccess: (data) => {
      setResult(data);
      if (data.success) {
        // Nach 2 Sekunden Seite neu laden um Premium-Status zu aktualisieren
        setTimeout(() => {
          window.location.reload();
        }, 2000);
        onSuccess?.();
      }
    },
    onError: (error) => {
      setResult({ success: false, message: error.message });
    },
  });

  const handleRedeem = () => {
    if (!code.trim()) return;
    setResult(null);
    redeemMutation.mutate({ code: code.trim() });
  };

  if (!showInput && !compact) {
    return (
      <button
        onClick={() => setShowInput(true)}
        className="text-sm text-[#c9a96e]/70 hover:text-[#c9a96e] transition-colors flex items-center gap-1.5 mt-3"
      >
        <Gift className="w-3.5 h-3.5" />
        Gutschein-Code einlösen
      </button>
    );
  }

  if (compact && !showInput) {
    return (
      <button
        onClick={() => setShowInput(true)}
        className="text-xs text-[#c9a96e]/60 hover:text-[#c9a96e] transition-colors flex items-center gap-1 mt-1"
      >
        <Gift className="w-3 h-3" />
        Code einlösen
      </button>
    );
  }

  return (
    <div className={compact ? "mt-2" : "mt-3"}>
      <div className="flex gap-2">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="Code eingeben"
          className={`${compact ? "h-8 text-xs" : "h-9 text-sm"} bg-white/5 border-[#c9a96e]/20 text-[#c9a96e] placeholder:text-[#c9a96e]/30 uppercase tracking-wider font-mono`}
          onKeyDown={(e) => e.key === "Enter" && handleRedeem()}
          disabled={redeemMutation.isPending}
        />
        <Button
          onClick={handleRedeem}
          disabled={!code.trim() || redeemMutation.isPending}
          size={compact ? "sm" : "default"}
          className={`${compact ? "h-8 px-3 text-xs" : "h-9 px-4 text-sm"} bg-[#c9a96e] hover:bg-[#b8944f] text-white shrink-0`}
        >
          {redeemMutation.isPending ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            "Einlösen"
          )}
        </Button>
      </div>

      {result && (
        <div className={`flex items-start gap-1.5 mt-2 ${result.success ? "text-green-500" : "text-red-400"} ${compact ? "text-xs" : "text-sm"}`}>
          {result.success ? (
            <CheckCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          ) : (
            <XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          )}
          <span>{result.message}</span>
        </div>
      )}
    </div>
  );
}
